import React, { useState } from 'react'

const PRESERVE_OPTIONS = ['Main product', 'Color palette', 'Visual style', 'Copy / headline', 'Logo', 'Promo elements'] as const
const PLACEMENT_OPTIONS = ['Social', 'Display', 'Print', 'Email', 'Other'] as const

export interface PromptBuilderData {
  editIntent: string
  preserve: Set<string>
  toneAudience: string
  placement: string | null
  mustHaveText: string
}

const PromptBuilderCard: React.FC<{
  onSubmit: (data: PromptBuilderData) => void
  onCancel: () => void
}> = ({ onSubmit, onCancel }) => {
  const [data, setData] = useState<PromptBuilderData>({
    editIntent: '',
    preserve: new Set(),
    toneAudience: '',
    placement: null,
    mustHaveText: '',
  })

  const togglePreserve = (item: string) => {
    setData(prev => {
      const next = new Set(prev.preserve)
      if (next.has(item)) next.delete(item); else next.add(item)
      return { ...prev, preserve: next }
    })
  }

  return (
    <div className="bg-gradient-to-b from-purple-50 to-white rounded-lg border border-purple-200 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Prompt Builder</span>
        <button onClick={onCancel} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">Cancel</button>
      </div>

      <div>
        <label className="text-[11px] font-medium text-gray-700 uppercase tracking-wide">What to change</label>
        <textarea
          value={data.editIntent}
          onChange={e => setData(prev => ({ ...prev, editIntent: e.target.value }))}
          placeholder="e.g. Add a summer vibe, change background to beach..."
          rows={2}
          className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 resize-none text-gray-900 placeholder:text-gray-400"
        />
      </div>

      <div>
        <label className="text-[11px] font-medium text-gray-700 uppercase tracking-wide">Elements to preserve</label>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {PRESERVE_OPTIONS.map(item => (
            <button
              key={item}
              onClick={() => togglePreserve(item)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                data.preserve.has(item)
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-purple-300'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[11px] font-medium text-gray-700 uppercase tracking-wide">Tone & audience</label>
        <input
          value={data.toneAudience}
          onChange={e => setData(prev => ({ ...prev, toneAudience: e.target.value }))}
          placeholder="e.g. luxury feminine, playful Gen-Z, corporate clean"
          className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-gray-900 placeholder:text-gray-400"
        />
      </div>

      <div>
        <label className="text-[11px] font-medium text-gray-700 uppercase tracking-wide">Placement</label>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {PLACEMENT_OPTIONS.map(item => (
            <button
              key={item}
              onClick={() => setData(prev => ({ ...prev, placement: prev.placement === item ? null : item }))}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                data.placement === item
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-purple-300'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[11px] font-medium text-gray-700 uppercase tracking-wide">Must-have text <span className="text-gray-400 normal-case">(optional)</span></label>
        <input
          value={data.mustHaveText}
          onChange={e => setData(prev => ({ ...prev, mustHaveText: e.target.value }))}
          placeholder="e.g. Shop now - 30% off"
          className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-gray-900 placeholder:text-gray-400"
        />
      </div>

      <button
        onClick={() => onSubmit(data)}
        disabled={!data.editIntent.trim()}
        className="w-full py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-40 transition-colors"
      >
        Craft prompt →
      </button>
    </div>
  )
}

export default PromptBuilderCard
